// @flow
import React from 'react';
import isFunction from 'lodash/isFunction';
import { defineMessages, FormattedMessage, injectIntl } from 'react-intl';
import type { formatMessage as formatMessageType } from 'react-intl';
import LocateContainer from 'brastrap/editorial/locate-container/LocateContainer';
import SectionNav from 'brastrap/common/section-nav/SectionNav';
import Markdown from 'brastrap/editorial/markdown/Markdown';
import Ellipsis from 'brastrap/common/ellipsis/Ellipsis';
import Message from 'brastrap/common/message/Message';
import Accordion from 'brastrap/common/accordion/Accordion';
import Container from 'brastrap/common/container/Container';
import type {
  InlineShopFinderProps as Props,
  Shop,
  PostCode,
  ShopParam,
} from './shop-finder.types';

const shopDistanceDescription =
  '__Address:__ {fullSiteAddress}\n\n' +
  '__Distance:__ _{distanceDriving}_ miles';

const shopDescription = '__Address:__ {fullSiteAddress}';

const noOp = () => {};

/**
 * @returns {Object}
 */
const messages = defineMessages({
  errorTitle: { id: 'messages.title.warning', defaultMessage: 'Error' },
  listingsTitle: {
    id: 'shop-finder.listings-title',
    defaultMessage: 'Choose from List of nearest shops',
  },
  noResults: {
    id: 'shop-finder.no-results',
    defaultMessage: 'Sorry, no shops found.',
  },
  postCodeTitle: {
    id: 'shop-finder.postcode-title',
    defaultMessage: 'Choose from list of postcodes',
  },
  postCodeNote: {
    id: 'shop-finder.postcode-note',
    defaultMessage: '__Did you mean:__ {name}, {county}, {postCode}?',
  },
  shopDescription: {
    id: 'shop-finder.shop-description',
    defaultMessage: shopDescription,
  },
  shopDistanceDescription: {
    id: 'shop-finder.shop-distance-description',
    defaultMessage: shopDistanceDescription,
  },
  title: {
    id: 'shops-finder.title',
    defaultMessage: 'Find your nearest Bravissimo',
  },
  viewAll: { id: 'shop-finder.view-all', defaultMessage: 'View all shops' },
  showShops: {
    id: 'shop-finder-accordion.title',
    defaultMessage: 'Your nearest shops',
  },
});

/**
 * @param {Object} shops
 * @param {Function} onSelect
 * @param {Function} formatMessage
 * @returns {Object}
 */
const formatShopsToSectionNavItems = (
  shops: Array<Shop>,
  onSelect: (shop: ShopParam) => void,
  formatMessage: formatMessageType
) =>
  shops.map((shop: Shop) => ({
    label: shop.name,
    note: shop.distanceDriving ? (
      <Markdown
        content={formatMessage(messages.shopDistanceDescription, shop)}
      />
    ) : (
      <Markdown content={formatMessage(messages.shopDescription, shop)} />
    ),
    onClick: isFunction(onSelect)
      ? e => {
          e.preventDefault();
          onSelect(shop);
        }
      : noOp,
    url: `/shops/${shop.slug}/`,
  }));

/**
 * @param {Object} postCodes
 * @param {Function} onSelect
 * @param {Function} formatMessage
 * @returns {Object}
 */
const formatPostCodesToSectionNavItems = (
  postCodes: Array<PostCode>,
  onSelect: (shop: ShopParam) => void,
  formatMessage: formatMessageType
) =>
  postCodes.map((postCode: PostCode) => ({
    label: postCode.postCode,
    note: postCode.name ? (
      <Markdown content={formatMessage(messages.postCodeNote, postCode)} />
    ) : null,
    onClick: isFunction(onSelect)
      ? e => {
          e.preventDefault();
          onSelect(postCode.postCode);
        }
      : noOp,
    url: '#',
  }));

const InlineShopFinder = (props: Props) => {
  const {
    displayInModal,
    displayTitle,
    errorMessage,
    fields: { location },
    intl: { formatMessage },
    isFetching,
    handleSubmit,
    modifiers,
    onBrowse,
    onChoosePostCode,
    onSelect,
    noResults,
    postCodes,
    shops,
  } = props;

  const locateProps: {
    location: string,
    onBrowse: HTMLButtonElement => void,
    onSubmit: HTMLButtonElement => void,
    title?: string,
  } = {
    location,
    onBrowse,
    onSubmit: handleSubmit,
  };

  if (displayTitle) {
    locateProps.title = formatMessage(messages.title);
  }

  return (
    <div className="c-shop-finder">
      <LocateContainer {...locateProps} modifiers={modifiers} />
      {errorMessage && (
        <Message
          title={formatMessage(messages.errorTitle)}
          modifiers={['banner', 'warn']}
          dismissable={false}
        >
          {errorMessage}
        </Message>
      )}

      {noResults && (
        /* No results found. Show message. */
        <Message
          title={formatMessage(messages.errorTitle)}
          modifiers={['banner', 'inform']}
          dismissable={false}
        >
          <FormattedMessage {...messages.noResults} />
        </Message>
      )}

      {isFetching ? (
        <Ellipsis modifiers={['small']} />
      ) : (
        <Container>
          {(!!shops.length || !!postCodes.length) && (
            <div className="c-shop-finder__listings">
              {!!shops.length && !displayInModal && (
                <Accordion activeChild="shop-finder-accordion">
                  <Accordion.Item
                    isActive={!!shops.length}
                    id="shop-finder-accordion"
                    label={props.intl.formatMessage(messages.showShops)}
                    needsResize={false}
                  >
                    <SectionNav
                      id="shop-finder-listings"
                      items={formatShopsToSectionNavItems(
                        shops,
                        onSelect,
                        formatMessage
                      )}
                      title={formatMessage(messages.listingsTitle)}
                    />
                  </Accordion.Item>
                </Accordion>
              )}

              {!!shops.length && displayInModal && (
                <SectionNav
                  id="shop-finder-listings"
                  items={formatShopsToSectionNavItems(
                    shops,
                    onSelect,
                    formatMessage
                  )}
                  title={formatMessage(messages.listingsTitle)}
                />
              )}

              {!!postCodes.length && (
                <SectionNav
                  id="shop-finder-postcodes"
                  items={formatPostCodesToSectionNavItems(
                    postCodes,
                    onChoosePostCode,
                    formatMessage
                  )}
                  title={formatMessage(messages.postCodeTitle)}
                />
              )}
            </div>
          )}
        </Container>
      )}
    </div>
  );
};

InlineShopFinder.defaultProps = {
  displayTitle: true,
  isFetching: false,
  modifiers: ['highlight'],
  noResults: false,
  postCodes: [],
  shops: [],
  displayInModal: false,
};

export default injectIntl(InlineShopFinder);
